/**
 * Hook to fetch and manage race data
 * Loads race info, aid stations, and initial runner data
 */

import { useState, useEffect } from 'react';
import { fetchRaceBySlug, fetchAidStations } from '../lib/queries';
import { supabase } from '../lib/supabase';
import { normalizeRaceRow } from '../lib/raceMappers';
import type { Race, AidStation } from '../lib/types';

interface UseRaceDataResult {
  race: Race | null;
  stations: AidStation[];
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook to fetch race and station data by slug
 * @param slug - The URL slug for the race (e.g., 'copper-kings-100-2025')
 * @returns Race data, stations, loading state, and error message
 */
export function useRaceData(slug: string): UseRaceDataResult {
  const [race, setRace] = useState<Race | null>(null);
  const [stations, setStations] = useState<AidStation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadRaceData() {
      try {
        setLoading(true);
        setError(null);

        // Fetch race by slug. The select list always includes elevation_profile
        // now — see fetchRaceBySlug's doc comment for why the old two-variant
        // split (with/without elevation) no longer exists.
        const raceData = await fetchRaceBySlug(slug);

        if (!isMounted) return;

        if (!raceData) {
          setError('Race not found');
          setLoading(false);
          return;
        }

        setRace(raceData);

        // Fetch aid stations for this race
        const stationsData = await fetchAidStations(raceData.id);

        if (!isMounted) return;

        setStations(stationsData);
        setLoading(false);
      } catch (err) {
        if (!isMounted) return;

        console.error('Error loading race data:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load race data'
        );
        setLoading(false);
      }
    }

    loadRaceData();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [slug]);

  /**
   * Subscribe to race metadata updates using Supabase Realtime
   * This enables instant status updates when race starts/completes
   * without requiring manual page refresh
   *
   * NOTE: Realtime does NOT send full rows on UPDATE — it sends only the
   * columns in the `supabase_realtime` publication's explicit whitelist for
   * this table (see 20260705100002_merge_catchup_access.sql, "the anon
   * whitelist"). The old elevation_data TSV was on that whitelist, which is
   * why ~2 MB was broadcast on every update; the derived elevation_profile is
   * ~8 KB, small enough that once the whitelist includes it there's no need
   * to filter it back out. But a column being absent from the whitelist means
   * it's absent from the payload entirely, not present-as-null — see the
   * elevation_profile handling below for why that distinction matters.
   */
  useEffect(() => {
    if (!race?.id) {
      return; // Don't subscribe until we have a race ID
    }

    console.log(`Setting up Realtime subscription for race ${race.id}`);

    // Create channel for this specific race.
    // races → race_events (dictionary §3.12): the metadata channel now watches
    // the renamed table. The `id` filter column keeps its name on race_events.
    const channel = supabase
      .channel(`race-${race.id}-metadata`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'race_events',
          filter: `id=eq.${race.id}`,
        },
        (payload) => {
          console.log('Race metadata updated via Realtime:', payload.new);

          // Realtime UPDATE payloads carry the RAW race_events row (wire name
          // `date_start`, status value `planning`). normalizeRaceRow applies the
          // same date_start→date rename and planning→draft mapping as the initial
          // fetch, so a live status/date change can't leak an un-mapped value.
          const rawRow = payload.new as Record<string, unknown>;

          // Realtime sends only the columns in the publication's whitelist, NOT
          // the full row (see 20260705100002_merge_catchup_access.sql). If
          // elevation_profile is not in that list, the payload has no such key —
          // and normalizeRaceRow would turn "absent" into null, blanking the
          // chart on every unrelated event update. Absent means "unchanged", so
          // carry the profile we already have; only an explicit null clears it.
          // TODO: remove this absent-key special case once a migration adds
          // elevation_profile to the supabase_realtime publication.
          setRace(prev => {
            const next = normalizeRaceRow(rawRow);
            if (!('elevation_profile' in rawRow) && prev) {
              next.elevation_profile = prev.elevation_profile;
            }
            return next;
          });
        }
      )
      .subscribe((status) => {
        console.log(`Race metadata subscription status: ${status}`);
      });

    // Cleanup: unsubscribe when component unmounts or race changes
    return () => {
      console.log(`Unsubscribing from race ${race.id} metadata updates`);
      channel.unsubscribe();
    };
  }, [race?.id]); // Re-subscribe if race ID changes

  return { race, stations, loading, error };
}

